import { Colors, Language, Lightning, Storage } from "@lightningjs/sdk";
import configs from "../../../lib/configs";
import { List } from "@lightningjs/ui";
import theme from "../../../lib/theme";
import { Button } from "../../input/Button/Button";
import { Icon } from "../Icon/Icon";
import { TranslatableText } from "../TranslatableText/TranslatableText";
import { getCountryName, getLanguageName, STORAGE_KEYS } from "../../../lib/utils";

interface FilterTemplateSpec extends Lightning.Component.TemplateSpec {
    Text: any;
    Icon: any;
}

class FilterItem
    extends Lightning.Component<FilterTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<FilterTemplateSpec>
{
    _value!: {
        key: string;
        group: string;
    };
    _selected: boolean = false;

    static override _template(): Lightning.Component.Template<FilterTemplateSpec> {
        return {
            w: (w) => w,
            alpha: 0.75,
            Icon: {
                w: 18,
                h: 18,
                x: -22,
                y: (h) => h / 2 - 2,
                mountY: 0.5,
                type: Icon,
                colorValue: theme.colors.bg.primary,
                icon: "check",
                iconWidth: 18,
                alpha: 0
            },
            Text: {
                pivotY: 0.5,
                pivotX: 0,
                scale: 1,
                w: (w) => w,
                type: TranslatableText,
                text: {
                    text: "",
                    fontSize: 18,
                    textColor: Colors(theme.colors.bg.primary).get()
                }
            }
        };
    }

    readonly Icon = this.getByRef("Icon")!;

    override _handleEnter() {
        this._selected = !this._selected;
        this.Icon.patch({
            smooth: {
                alpha: this._selected
            }
        });

        this.fireAncestors("$filterToggle", {
            ...this._value,
            selected: this._selected
        });
    }

    override _focus() {
        this.patch({
            Text: {
                smooth: {
                    scale: 1.15
                }
            },
            smooth: {
                alpha: 1
            }
        });
    }

    override _unfocus() {
        this.patch({
            Text: {
                smooth: {
                    scale: 1
                }
            },
            smooth: {
                alpha: 0.75
            }
        });
    }

    set content(value: any) {
        this._value = value;
        this.getByRef("Text")!.patch({
            backupValue: value.label,
            key: `category.${value.key}`
        });
    }

    set selected(selected: boolean) {
        this._selected = selected;

        this.Icon.patch({
            smooth: {
                alpha: this._selected
            }
        });
    }

    get selected(): boolean {
        return this._selected;
    }
}

interface FilterGroupTemplateSpec extends Lightning.Component.TemplateSpec {
    Text: typeof TranslatableText;
    List: any;
}

class FilterGroup
    extends Lightning.Component<FilterGroupTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<FilterGroupTemplateSpec>
{
    _selectedFilters: string[] = [];

    static override _template(): Lightning.Component.Template<FilterGroupTemplateSpec> {
        return {
            w: (w) => w,
            x: 30,
            Text: {
                h: 24,
                w: (w) => w,
                type: TranslatableText,
                text: {
                    text: "",
                    fontSize: 18,
                    textColor: Colors(theme.colors.bg.primary).get(),
                    fontFace: "SemiBold"
                }
            },
            List: {
                h: (h) => h - 30,
                w: (w) => w,
                y: 30,
                type: List,
                collision: true,
                direction: "column",
                spacing: 6
            }
        };
    }

    readonly List = this.getByRef("List")!;

    override _getFocused(): Lightning.Component | null | undefined {
        return this.List;
    }

    $filterToggle({ group, key, selected }: { selected: boolean; group: string; key: string }) {
        if (!selected) {
            this._selectedFilters = this._selectedFilters.filter((s: string) => s !== key);
        } else {
            this._selectedFilters.push(key);
        }

        this.fireAncestors("$filterToggle", { group, key, selected });
    }

    get selectedFilters() {
        return this._selectedFilters;
    }

    set selectedFilters(value: string[]) {
        this._selectedFilters = value;
    }

    set content(value: any) {
        this.getByRef("Text")!.patch({
            backupValue: value.name.charAt(0).toUpperCase() + value.name.slice(1),
            key: `filter.${value.name}`
        });

        // Get translations and sort alphabetically
        const itemsWithTranslations = value.items.map((filter: any) => {
            const translationKey = `category.${filter.key}`;
            const translatedText = Language.translate(translationKey);
            const displayText = translatedText !== translationKey ? translatedText : filter.label;
            return {
                ...filter,
                displayText,
                translationKey
            };
        });

        // Sort alphabetically based on translated text
        itemsWithTranslations.sort((a: any, b: any) => {
            return a.displayText.localeCompare(b.displayText);
        });

        this.getByRef("List").patch({
            items: itemsWithTranslations.map((filter: any) => ({
                type: FilterItem,
                content: { ...filter, group: value.name },
                h: 24,
                w: 260,
                selected: this._selectedFilters.includes(filter.key)
            }))
        });
    }
}

interface MediaFiltersTemplateSpec extends Lightning.Component.TemplateSpec {
    showFilterCategories: boolean;
    selectedCategory?: number | string;

    Button: any;
    Filters: any;
    Background: object;
    CloseButton: any;
}

export class MediaFilters
    extends Lightning.Component<MediaFiltersTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<MediaFiltersTemplateSpec>
{
    readonly Button = this.getByRef("Button")!;
    readonly Filters = this.getByRef("Filters")!;
    readonly Background = this.getByRef("Background")!;
    readonly CloseButton = this.getByRef("CloseButton")!;

    _filters: any = {};
    _showFilterCategories = true;

    static override _template(): Lightning.Component.Template<MediaFiltersTemplateSpec> {
        return {
            alpha: 0,
            Background: {
                rect: true,
                color: Colors("#ffffff").get(),
                y: -40,
                x: -1,
                h: theme.layout.h - 40,
                texture: Lightning.Tools.getRoundRect(
                    264,
                    theme.layout.h - 40,
                    10,
                    0,
                    Colors("#ffffff").get(),
                    true,
                    Colors("#ffffff").get()
                ),
                zIndex: 20,
                scaleY: 0,
                pivotY: 0
            },
            Button: {
                type: Button,
                label: "general.filters",
                w: 260,
                h: 56,
                y: -40,
                fontSize: theme.mediaItem.font.size
            },
            Filters: {
                y: 56,
                w: 260,
                pivotY: 0,
                scaleY: 0,
                clipping: true,
                // y: 20,
                // - offsets - top btn - bottom btn
                h: theme.layout.h - 30 - 56 - 150,
                type: List,
                collision: true,
                direction: "column",
                zIndex: 30,
                spacing: 16
            },
            CloseButton: {
                type: Button,
                reverseColor: true,
                label: "general.close",
                w: 153,
                h: 36,
                y: theme.layout.h - 156,
                x: 260 / 2,
                mountX: 0.5,
                fontSize: theme.mediaItem.font.size,
                pivotY: 0,
                scaleY: 0
            }
        };
    }

    override _setup() {
        if (configs?.layout?.filters?.enabled) {
            let filters = Storage.get("filters");

            if (!this._showFilterCategories) {
                delete filters.genres;
            }

            const total = Object.keys(filters)?.length;

            if (!total) return;

            let totalHeight = 0;
            const items = Object.keys(filters).map((group: any) => {
                const h = (filters[group].length + 1) * 24 + filters[group].length * 6;
                totalHeight += h;
                return {
                    type: FilterGroup,
                    selectedFilters: this._filters[group] || [],
                    content: {
                        name: group,
                        items: filters[group].map((item: any) => {
                            let label = item.key.charAt(0).toUpperCase() + item.key.slice(1);

                            if (group === "languages") {
                                label = getLanguageName(String(item.key));
                            } else if (group === "countries") {
                                label = getCountryName(String(item.key));
                            }

                            return {
                                ...item,
                                label
                            };
                        })
                    },
                    h: h,
                    w: 260
                };
            });

            this.patch({
                alpha: 1
            });

            this.Filters.patch({
                spacing: totalHeight < this.Filters.h ? Math.min(56, (this.Filters.h - totalHeight) / (total - 1)) : 16,
                items: items
            });
        }
    }

    override _getFocused(): Lightning.Component | null | undefined {
        return this.Button;
    }

    override _handleEnter(): void {
        this._setState("FilterState");

        this.signal("toggleFilters", true);
    }

    _close() {
        this.signal("toggleFilters", false);

        this._closeWithoutSignal();
    }

    _closeWithoutSignal() {
        this._setState("");

        this.Background.patch({
            smooth: {
                scaleY: 0
            }
        });
        this.Filters.patch({
            smooth: {
                scaleY: 0
            }
        });

        this.CloseButton.patch({
            smooth: {
                scaleY: 0
            }
        });

        this.Button.patch({
            isActive: false
        });
    }

    $filterToggle({ group, key, selected }: { selected: boolean; group: string; key: string }) {
        let selection = this._filters[group] || [];
        if (!selected) {
            selection = selection.filter((s: string) => s !== key);
        } else {
            selection.push(key);
        }

        this._filters[group] = selection;

        this.signal("search", {
            ...this._filters
        });
    }

    static override _states() {
        return [
            class FilterState extends this {
                override _getFocused() {
                    return this.Filters;
                }

                override _handleBack(): void {
                    this._close();
                }

                override _handleEnter(): void {}

                override _handleUp(): void {}

                override _handleDown(): void {
                    this._setState("CloseButtonState");
                }

                override $enter() {
                    this.Background.patch({
                        smooth: {
                            scaleY: 1
                        }
                    });
                    this.Filters.patch({
                        smooth: {
                            scaleY: 1
                        }
                    });
                    this.CloseButton.patch({
                        smooth: {
                            scaleY: 1
                        }
                    });
                    this.Button.patch({
                        isActive: true
                    });
                }
            },
            class CloseButtonState extends this {
                override _getFocused() {
                    return this.CloseButton;
                }

                override _handleBack(): void {
                    this._close();
                }

                override _handleEnter(): void {
                    this._close();
                }

                override _handleUp(): void {
                    this._setState("FilterState");
                }

                override _handleDown(): void {
                    this._setState("CloseButtonState");
                }
            }
        ];
    }

    set showFilterCategories(value: boolean) {
        this._showFilterCategories = value;

        this._setup();
    }

    get filters() {
        return this._filters;
    }

    set filters(value: any) {
        this._filters = value;
    }

    set selectedCategory(value: number | string) {
        this._filters["genres"] = [...(this._filters["genres"] || []), value];
    }
}
